"use client"

import { toast } from "@/components/Toast"
import { IconButton, Text } from "@/elements"
import { graphql } from "@/gql"
import { cache } from "@/gql-clients/experimental/cache"
import {
  ClosedJobPostingFragmentDoc,
  GetHirePageFilterCountsDocument,
  MyJobsActiveJobPostsClientQueryDocument,
  MyJobsActiveJobPostsCreativeQueryDocument,
  ReopenJobDocument,
} from "@/gql/graphql"
import { getHex } from "@/theme"
import { revalidateTagsHelper } from "@/utils/revalidateTagHelper"
import { useMutation } from "@apollo/client"
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline"
import * as Popover from "@radix-ui/react-popover"
import Link from "next/link"
import { FC } from "react"
import MessagesIcon from "../../[id]/(jobOwnerPage)/icons/MessagesIcon"
import FavoritesIcon from "../icons/FavoritesIcon"

import { ampli } from "@/ampli"
import { RefreshIcon } from "@/icons/outline"

interface Props {
  jobId: string | undefined
  typename: "ClientJobPosting" | "CreativeJobPosting"
}

graphql(`
  mutation ReopenJob($id: ID!) {
    reopenJobPosting(input: { id: $id }) {
      jobPosting {
        id
        closedAt
      }
      ok
    }
  }
`)

export const ClosedJobMenu: FC<Props> = ({ jobId, typename }) => {
  const item = cache.readFragment({
    fragmentName: "ClosedJobPosting",
    fragment: ClosedJobPostingFragmentDoc,
    id: `${typename}:${jobId}`,
  })

  const [reopenJobMutation] = useMutation(ReopenJobDocument)
  const handleReopenJob = () => {
    if (!jobId) {
      toast.error("Failed to reopen job")
      return
    }
    // ampli.closedJobQuickAction({
    //   action: "reopen",
    // })
    reopenJobMutation({
      variables: {
        id: jobId,
      },
      update(cache, { data }) {
        if (data?.reopenJobPosting?.ok) {
          cache.evict({
            id: cache.identify({ id: jobId, __typename: typename }),
          })
          cache.gc()
        }
      },
      refetchQueries: [
        GetHirePageFilterCountsDocument,
        MyJobsActiveJobPostsCreativeQueryDocument,
        MyJobsActiveJobPostsClientQueryDocument,
      ],
      awaitRefetchQueries: true,
      optimisticResponse(vars) {
        return {
          reopenJobPosting: {
            jobPosting: {
              ...item,
              id: vars.id,
              closedAt: null,
            },
            ok: true,
          },
        }
      },
      onError() {
        toast.error("Failed to reopen job")
      },
      onCompleted() {
        toast.success("Job reopened")
        revalidateTagsHelper(["hire-job-posts-published"])
      },
    })
  }

  return (
    <div className="flex items-center justify-center">
      <Popover.Root>
        <Popover.Trigger>
          <IconButton variant="white" Icon={EllipsisHorizontalIcon} />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={5}
            style={{
              boxShadow: "0px 8px 20px 0px rgba(0, 0, 0, 0.10)",
            }}
            className="mt-2 flex w-[260px] flex-col rounded-md border border-bg-3 bg-bg-1 p-2">
            <div
              onClick={handleReopenJob}
              className="flex cursor-pointer flex-row items-center gap-x-4 rounded-sm px-2 py-2 hover:bg-bg-2">
              <RefreshIcon color={getHex("fg-2")} />
              <Text variant="body1" className="text-fg-1">
                Reopen job
              </Text>
            </div>
            <Link
              href={`/hire/${jobId}?tab=applications`}
              // onClick={() => {
              // ampli.closedJobQuickAction({
              //   action: "viewApplicants",
              // })
              // }}
              className="flex flex-row items-center gap-x-4 rounded-sm px-2 py-2 hover:bg-bg-2">
              <FavoritesIcon color={getHex("fg-2")} />
              <Text variant="body1" className="text-fg-1">
                View applicants
              </Text>
            </Link>
            <Link
              href={`/hire/${jobId}?tab=messages`}
              // onClick={() => {
              //   ampli.closedJobQuickAction({
              //     action: "viewMessages",
              //   })
              // }}
              className="flex flex-row items-center gap-x-4 rounded-sm px-2 py-2 hover:bg-bg-2">
              <MessagesIcon width={20} height={20} color={getHex("fg-2")} />
              <Text variant="body1" className="text-fg-1">
                View messages
              </Text>
            </Link>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}
